/**
 * TaskTypesTab — Ustawienia → Typy zadań (słownik typów kartoteki Zadania, faza 30).
 *
 * Tabela typów z podglądem następnego numeru, okno edycji w `Modal`. Typ należy do JEDNEJ firmy
 * (po utworzeniu niezmienna), ma obowiązkowy wzór numeru; kod i polityka resetu numeracji są
 * blokowane przez serwer, gdy typ ma już zadania (409 `TYPE_NUMBERING_LOCKED`).
 * „Pole daty numeracji" (od której daty liczy się rok i miesiąc numeru; puste = data utworzenia)
 * wolno zmieniać zawsze; w tabeli ma własną kolumnę, w oknie edycji listę wyboru.
 *
 * Układ formularza karty zadania wybiera się w tabeli, w miejscu (kolumna „Układ formularza",
 * `LayoutPickerCell` wspólny z typami dokumentów); zapis idzie przez PATCH typu. Domyślny proces
 * (obieg akceptacji uruchamiany na nowym zadaniu tego typu) wybiera się tak samo: kolumna „Proces
 * domyślny" w miejscu (`DefaultProcessCell`) i pole w oknie typu (`DefaultProcessField`), oba
 * wspólne z typami dokumentów; lista = opublikowane procesy o celu „zadania".
 *
 * Uprawnienia (podpowiedź, rozstrzyga serwer): ekran wymaga `crm_core.task_types.view`
 * (bez niego — wyjaśnienie zamiast tabeli); dodawanie, edycja, włączanie i usuwanie wymagają
 * `crm_core.task_types.manage` (nieaktywne z wyjaśnieniem). „Pokaż usunięte" i „Przywróć" (jak w
 * typach dokumentów) — tylko z prawem zarządzania; usunięty typ ma w wierszu wyłącznie „Przywróć",
 * a edycja w miejscu (układ, proces, aktywność) jest na nim wyłączona.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { Eye, EyeOff, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { DefaultProcessField } from '@/components/kartoteki/DefaultProcessField';
import { FormLayoutField } from '@/components/kartoteki/FormLayoutField';
import { defaultProcessColumn, formLayoutColumn } from '@/components/kartoteki/recordTypeColumns';
import { Modal } from '@/components/Modal';
import { Can, PermissionButton } from '@/components/permissions/PermissionGate';
import { Spinner } from '@/components/Spinner';
import { Textarea } from '@/components/Textarea';
import { CompanyPicker } from '@/components/ui/CompanyPicker';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useAnyPermission, usePermission } from '@/hooks/usePermission';
import { api } from '@/lib/api';
import { numberingMaskError, previewDocumentNumber } from '@/lib/document-number-preview';
import { TASKS_MODULE_CODE } from '@/lib/processes/module-routes';
import {
  defaultProcessErrorMessage,
  useDefaultProcessOptions,
} from '@/lib/record-types/default-process';
import { formLayoutErrorMessage, type FormLayoutOption } from '@/lib/record-types/form-layout';
import {
  parseTaskApiError,
  TASK_TYPE_LIMITS,
  TASK_TYPES_PERMISSION,
  TASK_TYPES_QUERY_ROOT,
  taskErrorMessage,
  type TaskTypeDto,
  useTaskTypes,
} from '@/lib/tasks/task-api';
import { useTaskNumberingBaseFieldOptions } from '@/lib/tasks/useTaskNumberingBaseFieldOptions';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';
import { TASK_NUMBERING_RESETS, type TaskNumberingReset } from '@/pages/tasks/task-model';

interface TypeForm {
  companyId: string;
  code: string;
  name: string;
  description: string;
  numberingFormat: string;
  numberingReset: TaskNumberingReset;
  /** Slug pola daty numeracji; `''` = domyślnie (data utworzenia) — serwer dostaje wtedy `null`. */
  numberingBaseField: string;
  /** Id układu formularza karty zadania; `null` = domyślny układ kartoteki. */
  formLayoutId: string | null;
  /** Id domyślnego procesu (obiegu akceptacji) zadań tego typu; `null` = brak. */
  defaultProcessId: string | null;
  sortOrder: string;
  isActive: boolean;
}

/** Pole formularza, pod którym pokazujemy błąd zwrócony przez serwer. */
type ServerFieldKey =
  | 'code'
  | 'name'
  | 'numberingFormat'
  | 'numberingReset'
  | 'numberingBaseField'
  | 'formLayoutId'
  | 'defaultProcessId';

type ServerErrors = Partial<Record<ServerFieldKey, string>>;

const SERVER_FIELD_BY_CODE: Readonly<Record<string, ServerFieldKey>> = {
  CODE_TAKEN: 'code',
  NAME_TAKEN: 'name',
  NUMBERING_FORMAT_REQUIRED: 'numberingFormat',
  INVALID_NUMBERING_FORMAT: 'numberingFormat',
  NUMBERING_FORMAT_NO_COUNTER: 'numberingFormat',
  NUMBERING_FORMAT_RESET_MISMATCH: 'numberingFormat',
  NUMBERING_FORMAT_CONFLICT: 'numberingFormat',
  INVALID_NUMBERING_BASE_FIELD: 'numberingBaseField',
  FORM_LAYOUT_NOT_FOUND: 'formLayoutId',
  FORM_LAYOUT_NOT_FORM: 'formLayoutId',
  FORM_LAYOUT_MODULE_MISMATCH: 'formLayoutId',
  DEFAULT_PROCESS_NOT_FOUND: 'defaultProcessId',
  DEFAULT_PROCESS_NOT_PUBLISHED: 'defaultProcessId',
  DEFAULT_PROCESS_MODULE_MISMATCH: 'defaultProcessId',
  DEFAULT_PROCESS_TYPE_NOT_ALLOWED: 'defaultProcessId',
  DEFAULT_PROCESS_NO_CLASSIFIERS: 'defaultProcessId',
};

/** Kody 409 przywrócenia typu, które pokazujemy jako ostrzeżenie (jak w typach dokumentów). */
const RESTORE_CONFLICT_CODES: ReadonlySet<string> = new Set([
  'CODE_TAKEN',
  'NAME_TAKEN',
  'NUMBERING_FORMAT_CONFLICT',
  'NOT_DELETED',
]);

const EMPTY_FORM: TypeForm = {
  companyId: '',
  code: '',
  name: '',
  description: '',
  numberingFormat: '',
  numberingReset: 'yearly',
  numberingBaseField: '',
  formLayoutId: null,
  defaultProcessId: null,
  sortOrder: '',
  isActive: true,
};

function formFromType(type: TaskTypeDto): TypeForm {
  return {
    companyId: type.companyId,
    code: type.code,
    name: type.name,
    description: type.description ?? '',
    numberingFormat: type.numberingFormat ?? '',
    numberingReset: type.numberingReset,
    numberingBaseField: type.numberingBaseField ?? '',
    formLayoutId: type.formLayoutId,
    defaultProcessId: type.defaultProcessId,
    sortOrder: String(type.sortOrder),
    isActive: type.isActive,
  };
}

function resetLabel(t: TFunction, reset: TaskNumberingReset): string {
  switch (reset) {
    case 'continuous':
      return t('tenant.tasks.types.reset.continuous');
    case 'yearly':
      return t('tenant.tasks.types.reset.yearly');
    case 'monthly':
      return t('tenant.tasks.types.reset.monthly');
  }
}

/** Ciało POST — `sortOrder` pominięty, gdy pusty (serwer nadaje kolejność na końcu listy firmy). */
function buildCreateBody(form: TypeForm): Record<string, unknown> {
  const body: Record<string, unknown> = {
    companyId: form.companyId,
    code: form.code.trim(),
    name: form.name.trim(),
    description: form.description.trim() === '' ? null : form.description.trim(),
    numberingFormat: form.numberingFormat.trim(),
    numberingReset: form.numberingReset,
    numberingBaseField: form.numberingBaseField === '' ? null : form.numberingBaseField,
    isActive: form.isActive,
  };
  // Układ formularza: tylko gdy wybrany (brak = domyślny układ kartoteki, serwer zapisuje null).
  if (form.formLayoutId !== null) body.formLayoutId = form.formLayoutId;
  // Domyślny proces: tylko gdy wybrany (brak = typ bez obiegu domyślnego).
  if (form.defaultProcessId !== null) body.defaultProcessId = form.defaultProcessId;
  if (form.sortOrder.trim() !== '') body.sortOrder = Number(form.sortOrder.trim());
  return body;
}

/**
 * Ciało PATCH — tylko pola zmienione względem zapisanego typu (`companyId` NIE wchodzi: serwer
 * odrzuca je jako niezapisywalne). Pusty obiekt = nic do zapisania.
 */
function buildPatchBody(form: TypeForm, original: TaskTypeDto): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (form.code.trim() !== original.code) body.code = form.code.trim();
  if (form.name.trim() !== original.name) body.name = form.name.trim();
  const description = form.description.trim() === '' ? null : form.description.trim();
  if (description !== original.description) body.description = description;
  if (form.numberingFormat.trim() !== (original.numberingFormat ?? '')) {
    body.numberingFormat = form.numberingFormat.trim();
  }
  if (form.numberingReset !== original.numberingReset) body.numberingReset = form.numberingReset;
  // Pole daty numeracji wolno zmieniać zawsze (także gdy typ ma już zadania).
  if (form.numberingBaseField !== (original.numberingBaseField ?? '')) {
    body.numberingBaseField = form.numberingBaseField === '' ? null : form.numberingBaseField;
  }
  if (form.sortOrder.trim() !== '' && Number(form.sortOrder.trim()) !== original.sortOrder) {
    body.sortOrder = Number(form.sortOrder.trim());
  }
  if (form.formLayoutId !== original.formLayoutId) body.formLayoutId = form.formLayoutId;
  // Domyślny proces: tylko gdy zmieniony; `null` czyści wskazanie.
  if (form.defaultProcessId !== original.defaultProcessId) {
    body.defaultProcessId = form.defaultProcessId;
  }
  if (form.isActive !== original.isActive) body.isActive = form.isActive;
  return body;
}

export function TaskTypesTab() {
  const { t } = useTranslation();
  return (
    <Can
      code={TASK_TYPES_PERMISSION.view}
      fallback={
        <Card padding="md" data-testid="task-types-denied">
          <p className="text-sm text-text-tertiary">{t('tenant.tasks.types.noAccess')}</p>
        </Card>
      }
    >
      <TaskTypesContent />
    </Can>
  );
}

function TaskTypesContent() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();
  const companyScope = useActiveCompanyScope();
  const canManage = usePermission(TASK_TYPES_PERMISSION.manage);
  const canViewOrManage = useAnyPermission([
    TASK_TYPES_PERMISSION.view,
    TASK_TYPES_PERMISSION.manage,
  ]);
  const manageReason = t(canManage.reasonKey ?? 'tenant.permissions.denied.tooltip');

  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);
  const companyOptions = useMemo(
    () => companies.map((c) => ({ id: c.id, legalName: c.name })),
    [companies],
  );
  const companyNameById = useMemo(
    () => new Map(companies.map((c) => [c.id, c.name] as const)),
    [companies],
  );

  // Widok usuniętych tylko z prawem zarządzania — serwer odpowiada 403 na `include_deleted` bez niego.
  // Prawo liczone w firmie aktywnej z top-bara (jak typy dokumentów): bez firmy w pytaniu silnik
  // uprawnień przepuszcza wyłącznie właściciela tenanta.
  const [showDeleted, setShowDeleted] = useState(false);
  const typesQ = useTaskTypes(
    showDeleted && canManage.allowed
      ? { includeDeleted: true, companyId: companyIdParam(companyScope) }
      : {},
  );
  const types = useMemo(() => typesQ.data ?? [], [typesQ.data]);

  const [editing, setEditing] = useState<TaskTypeDto | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TaskTypeDto | null>(null);
  const [pendingRestore, setPendingRestore] = useState<TaskTypeDto | null>(null);
  const [form, setForm] = useState<TypeForm>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<TypeForm>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>({});

  // Układy FORMULARZA kartoteki Zadania do kolumny „Układ formularza" i pola w oknie typu. Serwer
  // wpuszcza `task_types.view` ALBO `manage` (jak w typach dokumentów) — osoba z samym podglądem
  // widzi w kolumnie nazwę układu; bez żadnego z praw zapytanie nie wychodzi.
  const layoutsQ = useQuery({
    queryKey: [TASK_TYPES_QUERY_ROOT, 'form-layouts'],
    queryFn: async () => {
      const res = await api.get<{ layouts: FormLayoutOption[] }>('/task-types/form-layouts');
      return res.data.layouts;
    },
    enabled: canViewOrManage.allowed,
    retry: false,
  });
  const layoutOptions = useMemo(() => layoutsQ.data ?? [], [layoutsQ.data]);

  // Opublikowane procesy o celu „zadania" do kolumny „Proces domyślny" i pola w oknie typu (ten sam
  // zakres uprawnień co układy: podgląd widzi w kolumnie nazwę procesu).
  const { processes: processOptions, loading: processesLoading } = useDefaultProcessOptions(
    TASKS_MODULE_CODE,
    canViewOrManage.allowed,
  );

  const sortedTypes = useMemo(
    () => [...types].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [types],
  );

  const resetOptions: DropdownOption[] = TASK_NUMBERING_RESETS.map((reset) => ({
    value: reset,
    label: resetLabel(t, reset),
  }));

  // Pole daty numeracji: opcje rdzenne + pola dodatkowe dat kartoteki. Zapisany slug spoza WCZYTANEJ
  // listy (np. pole dodatkowe usunięte po wyborze) dokładamy jako opcję nieaktywną, żeby stan był
  // widoczny. Do czasu wczytania katalogu pól (i przy jego błędzie) lista jest niepełna — zapisany
  // slug pokazujemy zwyczajnie, a zmiana pola jest zablokowana.
  const {
    options: baseFieldOptions,
    isLoading: baseFieldsLoading,
    isError: baseFieldsError,
  } = useTaskNumberingBaseFieldOptions();
  const baseFieldsSettled = !baseFieldsLoading && !baseFieldsError;
  const baseFieldLabelBySlug = useMemo(
    () => new Map(baseFieldOptions.map((o) => [o.value, o.label] as const)),
    [baseFieldOptions],
  );
  const baseFieldDropdownOptions = useMemo<DropdownOption[]>(() => {
    if (
      form.numberingBaseField === '' ||
      baseFieldOptions.some((o) => o.value === form.numberingBaseField)
    ) {
      return baseFieldOptions;
    }
    return [
      ...baseFieldOptions,
      {
        value: form.numberingBaseField,
        label: baseFieldsSettled
          ? t('tenant.tasks.types.numberingBase.inactiveOption', {
              slug: form.numberingBaseField,
            })
          : form.numberingBaseField,
        disabled: baseFieldsSettled,
      },
    ];
  }, [baseFieldOptions, baseFieldsSettled, form.numberingBaseField, t]);

  const modalOpen = createOpen || editing !== null;

  function openCreate() {
    const next: TypeForm = {
      ...EMPTY_FORM,
      companyId: companyScope.mode === 'company' ? (companyScope.companyId ?? '') : '',
    };
    setForm(next);
    setInitialForm(next);
    setSubmitted(false);
    setServerErrors({});
    setEditing(null);
    setCreateOpen(true);
  }

  function openEdit(type: TaskTypeDto) {
    const next = formFromType(type);
    setForm(next);
    setInitialForm(next);
    setSubmitted(false);
    setServerErrors({});
    setCreateOpen(false);
    setEditing(type);
  }

  function closeModal() {
    setCreateOpen(false);
    setEditing(null);
    setServerErrors({});
  }

  function update<K extends keyof TypeForm>(key: K, value: TypeForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key in serverErrors) {
      setServerErrors((prev) => {
        const next: ServerErrors = { ...prev };
        delete next[key as ServerFieldKey];
        return next;
      });
    }
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const maskError = numberingMaskError(form.numberingFormat);
  const maskPreview = previewDocumentNumber(form.numberingFormat);
  const maskMissing = form.numberingFormat.trim() === '';
  const sortOrderValid = form.sortOrder.trim() === '' || /^\d+$/.test(form.sortOrder.trim());

  const codeTrimmed = form.code.trim();
  const nameTrimmed = form.name.trim();
  // Duplikat liczy się tylko wśród żywych typów (usunięty typ nie blokuje kodu ani nazwy).
  const sameCompanyOthers = types.filter(
    (x) => x.gcrecord === null && x.companyId === form.companyId && x.id !== editing?.id,
  );
  const codeDuplicate = sameCompanyOthers.some(
    (x) => x.code.toLowerCase() === codeTrimmed.toLowerCase(),
  );
  const nameDuplicate = sameCompanyOthers.some(
    (x) => x.name.toLowerCase() === nameTrimmed.toLowerCase(),
  );

  const companyMissing = !editing && form.companyId === '';
  const codeError =
    codeTrimmed === ''
      ? t('tenant.tasks.types.form.codeRequired')
      : codeDuplicate
        ? t('tenant.tasks.types.form.codeDuplicate')
        : undefined;
  const nameError =
    nameTrimmed === ''
      ? t('tenant.tasks.types.form.nameRequired')
      : nameDuplicate
        ? t('tenant.tasks.types.form.nameDuplicate')
        : undefined;
  const hasErrors =
    companyMissing ||
    codeError !== undefined ||
    nameError !== undefined ||
    maskMissing ||
    maskError !== null ||
    !sortOrderValid;

  function invalidateTypes() {
    void queryClient.invalidateQueries({ queryKey: [TASK_TYPES_QUERY_ROOT] });
  }

  /**
   * Pola formularza, pod którymi pokazujemy błąd serwera. `TYPE_NUMBERING_LOCKED` dotyczy kodu
   * i resetu numeracji — wskazujemy te z nich, które użytkownik zmienił względem zapisanego typu.
   */
  function fieldsForServerError(code: string | undefined): ServerFieldKey[] {
    if (code === undefined) return [];
    if (code === 'TYPE_NUMBERING_LOCKED') {
      const fields: ServerFieldKey[] = [];
      if (editing && form.code.trim() !== editing.code) fields.push('code');
      if (editing && form.numberingReset !== editing.numberingReset) fields.push('numberingReset');
      return fields;
    }
    const field = SERVER_FIELD_BY_CODE[code];
    return field === undefined ? [] : [field];
  }

  function handleSaveError(err: unknown, fallbackKey: string) {
    const { code } = parseTaskApiError(err);
    const message =
      formLayoutErrorMessage(code, t) ??
      defaultProcessErrorMessage(code, t) ??
      taskErrorMessage(err, t, fallbackKey);
    const next: ServerErrors = {};
    for (const field of fieldsForServerError(code)) next[field] = message;
    setServerErrors(next);
    toast.show({ variant: 'error', title: message });
    // Typ zniknął w międzyczasie (usunięty gdzie indziej) — nie ma czego już edytować.
    if (editing && code === 'NOT_FOUND') closeModal();
  }

  function showMutationError(err: unknown, fallbackKey: string) {
    toast.show({ variant: 'error', title: taskErrorMessage(err, t, fallbackKey) });
  }

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (editing) {
        const body = buildPatchBody(form, editing);
        if (Object.keys(body).length === 0) return;
        await api.patch(`/task-types/${editing.id}`, body);
        return;
      }
      await api.post('/task-types', buildCreateBody(form));
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: editing
          ? t('tenant.tasks.types.toast.updated')
          : t('tenant.tasks.types.toast.created'),
      });
      closeModal();
    },
    onError: (err) => handleSaveError(err, 'tenant.tasks.types.toast.saveError'),
    // Lista typów odświeża się także po błędzie — typ mógł się zmienić albo zniknąć w międzyczasie.
    onSettled: invalidateTypes,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (vars: { id: string; isActive: boolean }): Promise<void> => {
      await api.patch(`/task-types/${vars.id}`, { isActive: vars.isActive });
    },
    onError: (err) => showMutationError(err, 'tenant.tasks.types.toast.saveError'),
    onSettled: invalidateTypes,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/task-types/${id}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.tasks.types.toast.deleted') });
      setPendingDelete(null);
    },
    onError: (err) => {
      setPendingDelete(null);
      showMutationError(err, 'tenant.tasks.types.toast.deleteError');
    },
    onSettled: invalidateTypes,
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.post(`/task-types/${id}/restore`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.tasks.types.toast.restored') });
    },
    onError: (err) => {
      // Konflikty (kod, nazwa, wzór numeru zajęte przez żywy typ; już przywrócony) to ostrzeżenie.
      const { code } = parseTaskApiError(err);
      toast.show({
        variant: code !== undefined && RESTORE_CONFLICT_CODES.has(code) ? 'warning' : 'error',
        title: taskErrorMessage(err, t, 'tenant.tasks.types.toast.restoreError'),
      });
    },
    onSettled: () => {
      setPendingRestore(null);
      invalidateTypes();
    },
  });

  function handleSubmit() {
    setSubmitted(true);
    if (hasErrors) return;
    saveMutation.mutate();
  }

  const { mutate: toggleActive, isPending: toggleActivePending } = toggleActiveMutation;

  const layoutMutation = useMutation({
    mutationFn: async (vars: { id: string; formLayoutId: string | null }): Promise<void> => {
      await api.patch(`/task-types/${vars.id}`, { formLayoutId: vars.formLayoutId });
    },
    onSettled: invalidateTypes,
  });
  const { mutateAsync: saveLayout } = layoutMutation;

  /** Zapis układu z komórki: błąd wraca do popovera jako komunikat pod listą (popover zostaje otwarty). */
  const commitLayout = useCallback(
    async (row: TaskTypeDto, formLayoutId: string | null): Promise<void> => {
      try {
        await saveLayout({ id: row.id, formLayoutId });
      } catch (err) {
        const { code } = parseTaskApiError(err);
        throw new Error(
          formLayoutErrorMessage(code, t) ??
            taskErrorMessage(err, t, 'tenant.tasks.types.toast.saveError'),
          { cause: err },
        );
      }
    },
    [saveLayout, t],
  );

  const processMutation = useMutation({
    mutationFn: async (vars: { id: string; defaultProcessId: string | null }): Promise<void> => {
      await api.patch(`/task-types/${vars.id}`, { defaultProcessId: vars.defaultProcessId });
    },
    // Lista nie ma własnej powierzchni błędu — błąd serwera idzie toastem (jak w typach dokumentów).
    onError: (err) =>
      toast.show({
        variant: 'error',
        title:
          defaultProcessErrorMessage(parseTaskApiError(err).code, t) ??
          taskErrorMessage(err, t, 'tenant.tasks.types.toast.saveError'),
      }),
    onSettled: invalidateTypes,
  });
  const {
    mutate: saveProcess,
    isPending: processPending,
    variables: processVariables,
  } = processMutation;

  const columns = useMemo<ColumnDefinition<TaskTypeDto>[]>(
    () => [
      {
        id: 'company',
        accessor: (row) => companyNameById.get(row.companyId) ?? '',
        header: t('tenant.tasks.types.column.company'),
        headerFilter: { category: 'text' },
      },
      {
        id: 'code',
        accessor: (row) => row.code,
        header: t('tenant.tasks.types.column.code'),
        headerFilter: { category: 'text' },
        renderCell: (_v, row) => (
          <code className="rounded bg-bg-secondary px-1.5 py-0.5 text-xs text-text-secondary">
            {row.code}
          </code>
        ),
      },
      {
        id: 'name',
        accessor: (row) => row.name,
        header: t('tenant.tasks.types.column.name'),
        headerFilter: { category: 'text' },
        renderCell: (_v, row) => <span className="font-medium text-text-primary">{row.name}</span>,
      },
      {
        id: 'numbering',
        accessor: (row) => row.numberingFormat ?? '',
        header: t('tenant.tasks.types.column.numbering'),
        headerFilter: { category: 'text' },
        renderCell: (_v, row) => (
          <div className="flex flex-col">
            <code className="text-xs text-text-secondary">{row.numberingFormat ?? ''}</code>
            <span className="text-xs text-text-muted">
              {previewDocumentNumber(row.numberingFormat ?? '') ?? ''}
            </span>
          </div>
        ),
      },
      {
        id: 'reset',
        accessor: (row) => resetLabel(t, row.numberingReset),
        header: t('tenant.tasks.types.column.reset'),
        headerFilter: { category: 'none' },
      },
      {
        id: 'numberingBase',
        accessor: (row) => {
          const slug = row.numberingBaseField ?? '';
          const label = baseFieldLabelBySlug.get(slug);
          if (label !== undefined) return label;
          // Slug spoza listy: oznaczenie „nieaktywne" tylko, gdy lista pól się wczytała.
          return baseFieldsSettled
            ? t('tenant.tasks.types.numberingBase.inactiveOption', { slug })
            : slug;
        },
        header: t('tenant.tasks.types.column.numberingBase'),
        headerFilter: { category: 'none' },
      },
      defaultProcessColumn<TaskTypeDto>({
        t,
        header: t('tenant.recordTypes.defaultProcess.column'),
        processes: processOptions,
        getProcessId: (row) => row.defaultProcessId,
        canEdit: (row) => canManage.allowed && row.gcrecord === null,
        isPending: (row) => processPending && processVariables?.id === row.id,
        onChange: (row, nextId) => saveProcess({ id: row.id, defaultProcessId: nextId }),
        testId: (row) => `task-type-process-${row.id}`,
      }),
      formLayoutColumn<TaskTypeDto>({
        header: t('tenant.tasks.types.column.formLayout'),
        layouts: layoutOptions,
        noneLabel: t('tenant.recordTypes.formLayout.none'),
        getLayoutId: (row) => row.formLayoutId,
        canEdit: (row) => canManage.allowed && row.gcrecord === null,
        onCommit: commitLayout,
        testId: (row) => `task-type-layout-${row.id}`,
      }),
      {
        id: 'active',
        accessor: (row) => (row.isActive ? t('common.yes') : t('common.no')),
        header: t('tenant.tasks.types.column.active'),
        headerFilter: { category: 'none' },
        renderCell: (_v, row) => (
          <Checkbox
            checked={row.isActive}
            disabled={!canManage.allowed || toggleActivePending || row.gcrecord !== null}
            title={canManage.allowed ? undefined : manageReason}
            aria-label={t('tenant.tasks.types.activeFor', { name: row.name })}
            onChange={(e) => toggleActive({ id: row.id, isActive: e.target.checked })}
          />
        ),
      },
    ],
    [
      t,
      companyNameById,
      baseFieldLabelBySlug,
      baseFieldsSettled,
      canManage.allowed,
      manageReason,
      toggleActive,
      toggleActivePending,
      layoutOptions,
      commitLayout,
      processOptions,
      saveProcess,
      processPending,
      processVariables,
    ],
  );

  return (
    <div data-testid="task-types-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-text-secondary">{t('tenant.tasks.types.subtitle')}</p>
        <PermissionButton
          code={TASK_TYPES_PERMISSION.manage}
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={openCreate}
          data-testid="task-type-new-btn"
        >
          {t('tenant.tasks.types.add')}
        </PermissionButton>
      </header>

      {/* Przełącznik widoku usuniętych — tylko z prawem zarządzania (serwer bez niego da 403). */}
      {canManage.allowed && (
        <div className="mb-3 flex justify-end">
          <Button
            variant={showDeleted ? 'tertiary' : 'secondary'}
            size="sm"
            leftIcon={
              showDeleted ? (
                <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              )
            }
            aria-pressed={showDeleted}
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="task-types-show-deleted"
          >
            {t('tenant.tasks.types.showDeleted')}
          </Button>
        </div>
      )}

      {typesQ.isLoading ? (
        <div className="flex justify-center py-8" data-testid="task-types-loading">
          <Spinner />
        </div>
      ) : typesQ.isError ? (
        <Alert tone="error" data-testid="task-types-error">
          {taskErrorMessage(typesQ.error, t, 'tenant.tasks.types.loadError')}
        </Alert>
      ) : types.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.tasks.types.empty')}</p>
        </Card>
      ) : (
        <ConfigurableTable<TaskTypeDto>
          tableId="tenant-task-types"
          testIdPrefix="task-type"
          data={sortedTypes}
          getRowId={(row) => row.id}
          columns={columns}
          rowClassName={(row) => (row.isActive && row.gcrecord === null ? '' : 'opacity-50')}
          configurable={false}
          persistConfig={false}
          showViewModeToggle={false}
          stickyHeader
          renderActions={(row) => (
            <div className="flex justify-end gap-1">
              {row.gcrecord !== null ? (
                <IconButton
                  size="sm"
                  aria-label={t('tenant.tasks.types.restoreAction')}
                  disabled={!canManage.allowed}
                  title={canManage.allowed ? undefined : manageReason}
                  onClick={() => setPendingRestore(row)}
                  data-testid={`task-type-restore-${row.id}`}
                >
                  <RotateCcw />
                </IconButton>
              ) : (
                <>
                  <IconButton
                    size="sm"
                    aria-label={t('tenant.tasks.types.editAction')}
                    disabled={!canManage.allowed}
                    title={canManage.allowed ? undefined : manageReason}
                    onClick={() => openEdit(row)}
                    data-testid={`task-type-edit-${row.id}`}
                  >
                    <Pencil />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="danger"
                    aria-label={t('tenant.tasks.types.deleteAction')}
                    disabled={!canManage.allowed}
                    title={canManage.allowed ? undefined : manageReason}
                    onClick={() => setPendingDelete(row)}
                    data-testid={`task-type-delete-${row.id}`}
                  >
                    <Trash2 />
                  </IconButton>
                </>
              )}
            </div>
          )}
          actionsColumnWidth={110}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        size="lg"
        guardUnsaved
        dirty={isDirty}
        closeOnBackdrop={!saveMutation.isPending}
        closeOnEsc={!saveMutation.isPending}
        title={
          editing
            ? t('tenant.tasks.types.form.editTitle')
            : t('tenant.tasks.types.form.createTitle')
        }
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saveMutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="task-type-form"
              variant="primary"
              disabled={saveMutation.isPending}
              data-testid="task-type-form-save"
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <form
          id="task-type-form"
          className="grid grid-cols-1 gap-4 md:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {editing ? (
            <Input
              label={t('tenant.tasks.types.form.company')}
              value={companyNameById.get(form.companyId) ?? ''}
              hint={t('tenant.tasks.types.form.companyImmutableHint')}
              readOnly
              disabled
              data-testid="task-type-form-company-readonly"
            />
          ) : (
            <FormField
              label={t('tenant.tasks.types.form.company')}
              required
              error={
                submitted && companyMissing
                  ? t('tenant.tasks.types.form.companyRequired')
                  : undefined
              }
            >
              <CompanyPicker
                value={form.companyId || null}
                onChange={(id) => update('companyId', id ?? '')}
                companies={companyOptions}
                required
                testId="task-type-form-company"
              />
            </FormField>
          )}
          <div />
          <Input
            label={t('tenant.tasks.types.form.code')}
            required
            value={form.code}
            maxLength={TASK_TYPE_LIMITS.code}
            onChange={(e) => update('code', e.target.value)}
            error={submitted && codeError !== undefined ? codeError : serverErrors.code}
            data-testid="task-type-form-code"
          />
          <Input
            label={t('tenant.tasks.types.form.name')}
            required
            value={form.name}
            maxLength={TASK_TYPE_LIMITS.name}
            onChange={(e) => update('name', e.target.value)}
            error={submitted && nameError !== undefined ? nameError : serverErrors.name}
            data-testid="task-type-form-name"
          />
          <div className="md:col-span-2">
            <Textarea
              label={t('tenant.tasks.types.form.description')}
              rows={2}
              value={form.description}
              maxLength={TASK_TYPE_LIMITS.description}
              onChange={(e) => update('description', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Input
              label={t('tenant.tasks.types.form.numberingFormat')}
              required
              value={form.numberingFormat}
              maxLength={TASK_TYPE_LIMITS.numberingFormat}
              placeholder={t('tenant.tasks.types.form.numberingPlaceholder')}
              onChange={(e) => update('numberingFormat', e.target.value)}
              hint={
                maskPreview
                  ? t('tenant.tasks.types.form.numberingPreview', { number: maskPreview })
                  : t('tenant.tasks.types.form.numberingHint')
              }
              error={
                submitted && (maskMissing || maskError !== null)
                  ? t('tenant.tasks.types.form.numberingInvalid')
                  : serverErrors.numberingFormat
              }
              data-testid="task-type-form-numbering"
            />
          </div>
          <FormField label={t('tenant.tasks.types.form.reset')} error={serverErrors.numberingReset}>
            <Dropdown
              options={resetOptions}
              value={form.numberingReset}
              onChange={(v) => {
                const next = TASK_NUMBERING_RESETS.find((reset) => reset === v);
                if (next) update('numberingReset', next);
              }}
              data-testid="task-type-form-reset"
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField
              label={t('tenant.tasks.types.form.numberingBaseField')}
              hint={t('tenant.tasks.types.form.numberingBaseFieldHint')}
              error={
                serverErrors.numberingBaseField ??
                (baseFieldsError ? t('tenant.tasks.types.numberingBase.catalogError') : undefined)
              }
            >
              <Dropdown
                options={baseFieldDropdownOptions}
                value={form.numberingBaseField}
                disabled={!baseFieldsSettled}
                onChange={(v) => update('numberingBaseField', v)}
                data-testid="task-type-form-numbering-base-field"
              />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormLayoutField
              layouts={layoutOptions}
              unavailable={layoutsQ.isError}
              value={form.formLayoutId}
              onChange={(id) => update('formLayoutId', id)}
              error={serverErrors.formLayoutId}
              data-testid="task-type-form-form-layout"
            />
          </div>
          <div className="md:col-span-2">
            <DefaultProcessField
              processes={processOptions}
              loading={processesLoading}
              isCreate={!editing}
              value={form.defaultProcessId}
              onChange={(id) => update('defaultProcessId', id)}
              error={serverErrors.defaultProcessId}
              data-testid="task-type-form-default-process"
            />
          </div>
          <Input
            type="number"
            label={t('tenant.tasks.types.form.sortOrder')}
            value={form.sortOrder}
            hint={editing ? undefined : t('tenant.tasks.types.form.sortOrderHint')}
            onChange={(e) => update('sortOrder', e.target.value)}
            error={
              submitted && !sortOrderValid
                ? t('tenant.tasks.types.form.sortOrderInvalid')
                : undefined
            }
            data-testid="task-type-form-sort-order"
          />
          <div className="md:col-span-2">
            <Checkbox
              label={t('tenant.tasks.types.form.active')}
              checked={form.isActive}
              onChange={(e) => update('isActive', e.target.checked)}
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.tasks.types.delete.title')}
        message={t('tenant.tasks.types.delete.message', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingRestore !== null}
        title={t('tenant.tasks.types.restore.title')}
        message={t('tenant.tasks.types.restore.message', { name: pendingRestore?.name ?? '' })}
        confirmLabel={t('tenant.tasks.types.restoreAction')}
        variant="primary"
        onConfirm={() => {
          if (pendingRestore) restoreMutation.mutate(pendingRestore.id);
        }}
        onClose={() => setPendingRestore(null)}
      />
    </div>
  );
}
